import React from "react";
import {
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { connect } from "react-redux";
import { useEntitlementRepository } from "./entitlementContext";
import { isMlEntitlementEnabled } from "./entitlementMode";
import { entitlementDisplayMessage } from "./entitlementDisplayMessage";

const summaryStyle = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  marginRight: "min(3em, 5vw)",
  whiteSpace: "nowrap",
};

export const EntitlementProjectionSummary = ({ entitlement, noAi }) => {
  const repository = useEntitlementRepository();
  const theme = useTheme();
  if (!repository || !isMlEntitlementEnabled(noAi)) {
    return null;
  }

  const retry = () => repository?.refresh("manual");
  const projection = entitlement?.projection;
  const status = entitlement?.status || "idle";
  const error = entitlement?.error;
  const stale = status === "stale";

  if (!projection) {
    if (status === "error") {
      return (
        <div
          role="status"
          aria-live="polite"
          aria-label="Organization entitlement"
          style={summaryStyle}
        >
          <span>
            {entitlementDisplayMessage(error,
              "Entitlement information is temporarily unavailable.")}
          </span>
          <Button size="small" onClick={retry} disabled={!repository}>
            Retry
          </Button>
        </div>
      );
    }
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Organization entitlement"
        style={summaryStyle}
      >
        Loading entitlement…
      </div>
    );
  }

  const details = (
    <>
      <Typography
        variant="body2"
        component="div"
        style={{ color: "inherit" }}
      >
        <span style={{ color: "rgba(255, 255, 255, 0.7)" }}>Paid — </span>
        total ₹ {projection.paid.total}, reserved ₹ {projection.paid.reserved},
        available ₹ {projection.paid.available}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        style={{ color: "inherit" }}
      >
        <span style={{ color: "rgba(255, 255, 255, 0.7)" }}>Pro-bono — </span>
        total {projection.proBono.totalUnits} units, reserved{" "}
        {projection.proBono.reservedUnits} units, available{" "}
        {projection.proBono.availableUnits} units
      </Typography>
      {stale && (
        <Typography
          variant="body2"
          component="div"
          style={{ color: theme.palette.warning.main, marginTop: 4 }}
        >
          Values may be stale.{" "}
          {entitlementDisplayMessage(error, "Refresh to confirm current entitlement.")}
        </Typography>
      )}
    </>
  );

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Organization entitlement"
      style={summaryStyle}
    >
      <span>Available ₹ {projection.paid.available}</span>
      {stale && (
        <Button size="small" onClick={retry}>
          Refresh
        </Button>
      )}
      <Tooltip arrow title={details}>
        <IconButton
          aria-label="Show entitlement details"
          size="small"
          style={{
            padding: 2,
            color: stale ? theme.palette.warning.main : "inherit",
          }}
        >
          <InfoOutlinedIcon style={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const mapStateToProps = (store) => ({
  entitlement: store.entitlementStore,
  noAi: store.userStore?.userProfile?.no_ai_annotation,
});

export default connect(mapStateToProps)(EntitlementProjectionSummary);
